import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPrefixes, { DatasetFileConfig } from "datasets/hooks/useDatasetPrefixes";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { formatDistanceStrict, parseISO } from "date-fns";
// TODO(AA-1631): these components are MUI-based and should be rewritten in shadcn/Tailwind
import CreateFolderDialog from "files/components/CreateFolderDialog";
import DeleteFileDialog from "files/components/DeleteFileDialog";
import FileActionDialog from "files/components/FileActionDialog";
import FilePreview from "files/components/FilePreview";
import FileTree from "files/components/FileTree";
import UploadFileDialog from "files/components/UploadFileDialog";
import useFileAccess from "files/hooks/useFileAccess";
import useFiles, {
  BASE_DIRECTORIES,
  encodeObjectKey,
  resolveObjectKey,
  resolveRouterPath,
} from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { JobType } from "files/types/interfaces/fileJob";
import {
  ChevronDown,
  Copy,
  File,
  Folder,
  FolderPlus,
  MoreHorizontal,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import prettyBytes from "pretty-bytes";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { FileDownload } from "~/components/ui/file-download";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import useUser from "~/contexts/user";
import useUserPermission from "~/hooks/useUserPermission";
import { cn } from "~/lib/utils";
import { apiMutate } from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const PROJECT_PREFIX_REGEX = new RegExp(`^${BaseDirectory.PROJECTS}/\\d+/?$`);

const formatRelativeDate = (iso: string): string => {
  try {
    return formatDistanceStrict(parseISO(iso), new Date(), { addSuffix: true });
  } catch {
    return "";
  }
};

const getPrefixTitle = (prefix: string): string => {
  const routerPath = resolveRouterPath(prefix);
  return routerPath.split("/").filter(Boolean).at(-1) ?? prefix;
};

const getPrefixSubtitle = (prefix: string): string => resolveRouterPath(prefix);

const DirectoryHeader = () => (
  <div className="flex items-center gap-3 border-b border-border px-3 py-1.5 text-xs font-medium text-muted-foreground">
    <span className="flex-1">Name</span>
    <span className="w-20 text-right">Size</span>
    <span className="w-32 text-right">Updated</span>
    <span className="w-7" />
  </div>
);

interface PrefixRowProps {
  config: DatasetFileConfig;
  datasetId: string;
  canEdit: boolean;
  onRemoved: () => void;
}

const PrefixRow = ({ config, datasetId, canEdit, onRemoved }: PrefixRowProps) => {
  const { showError, showSuccess } = useToast();
  const title = getPrefixTitle(config.prefix);
  const subtitle = getPrefixSubtitle(config.prefix);
  const routerPath = encodeObjectKey(resolveRouterPath(config.prefix));
  const href = `/nextgen/datasets/${datasetId}/files/${config.id}/${routerPath}`;

  const handleCopyLink = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(`${window.location.origin}${href}`);
    showSuccess("Link copied to clipboard");
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await apiMutate(
        `${VITE_CATALOG_API}/datasets/${datasetId}/file-configs/${config.id}`,
        "DELETE",
        undefined,
        { expects204: true }
      );
      trackEvent("dataset_file_config_removed", {
        dataset_id: datasetId,
        folder_prefix: config.prefix,
      });
      onRemoved();
    } catch {
      showError("Unable to remove folder from dataset");
    }
  };

  return (
    <div className="group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-muted">
      <Link to={href} className="flex flex-1 min-w-0 items-center gap-3">
        <Folder className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0">
          <p className="truncate text-foreground">{title}</p>
          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </Link>
      <span className="w-20" />
      <span className="w-32" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-foreground/10"
            aria-label={`Actions for ${config.prefix}`}
            onClick={(e) => e.preventDefault()}
          >
            <MoreHorizontal className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DropdownMenuItem onClick={handleCopyLink}>
            <Copy />
            Copy link
          </DropdownMenuItem>
          {canEdit && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={handleRemove}>
                <Trash2 />
                Remove
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

interface FileItemRowProps {
  node: FileInfo;
  href: string;
  disabled?: boolean;
}

const FileItemRow = ({ node, href, disabled }: FileItemRowProps) => {
  const isDir = node.type === FileType.DIRECTORY;
  const isDisabled = !isDir && disabled;
  const name = node.key.split("/").filter(Boolean).pop() ?? node.key;
  const location = useLocation();
  const { showSuccess } = useToast();
  const canModify = useFileAccess(node.key, "write");
  const [fileAction, setFileAction] = useState<JobType | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const showActions =
    !node.pending_action &&
    !BASE_DIRECTORIES[resolveRouterPath(node.key)] &&
    !PROJECT_PREFIX_REGEX.test(node.key);

  const handleCopyLink = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(`${window.location.origin}${href}`);
    showSuccess("Link copied to clipboard");
  };

  const handleCopyS3Uri = (e: React.MouseEvent) => {
    e.preventDefault();
    if (node.s3_uri) {
      navigator.clipboard.writeText(node.s3_uri);
      showSuccess("Cloud path copied to clipboard");
    }
  };

  const linkTo = isDir ? href : { pathname: location.pathname, search: location.search };
  const linkState = isDir ? undefined : { preview: { key: node.key, size: node.size } };

  return (
    <>
      <div
        className={cn(
          "group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
          !isDisabled && "hover:bg-muted"
        )}
      >
        <Link
          to={isDisabled ? location.pathname : linkTo}
          state={isDisabled ? undefined : linkState}
          replace={!isDir}
          className={cn(
            "flex flex-1 min-w-0 items-center gap-3",
            isDisabled && "cursor-default opacity-50 pointer-events-none"
          )}
        >
          {isDir ? (
            <Folder className="size-4 shrink-0 text-primary" aria-hidden="true" />
          ) : (
            <File className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="flex-1 truncate text-foreground">{name}</span>
        </Link>
        <span className="w-20 text-right text-xs text-muted-foreground">
          {!isDir && node.size != null ? prettyBytes(node.size) : ""}
        </span>
        <span className="w-32 text-right text-xs text-muted-foreground">
          {node.updated_at ? formatRelativeDate(node.updated_at) : ""}
        </span>
        {showActions ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-foreground/10"
                aria-label={`Actions for ${name}`}
                onClick={(e) => e.preventDefault()}
              >
                <MoreHorizontal className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
              {!isDir && (
                <FileDownload fileKey={node.key}>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Download</DropdownMenuItem>
                </FileDownload>
              )}
              <DropdownMenuItem onClick={handleCopyLink}>
                <Copy />
                Copy link
              </DropdownMenuItem>
              {node.s3_uri && (
                <DropdownMenuItem onClick={handleCopyS3Uri}>
                  <Copy />
                  Copy S3 URI
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setFileAction(JobType.COPY)}>
                Copy {isDir ? "Folder" : "File"}...
              </DropdownMenuItem>
              {canModify && (
                <DropdownMenuItem onClick={() => setFileAction(JobType.RENAME)}>
                  Rename...
                </DropdownMenuItem>
              )}
              {canModify && (
                <DropdownMenuItem onClick={() => setFileAction(JobType.MOVE)}>
                  Move...
                </DropdownMenuItem>
              )}
              {canModify && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                    <Trash2 />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="w-7" />
        )}
      </div>
      {fileAction && (
        <FileActionDialog
          open
          fileKey={node.key}
          action={fileAction}
          isDirectory={isDir}
          handleClose={() => setFileAction(null)}
        />
      )}
      <DeleteFileDialog
        open={deleteOpen}
        fileKey={node.key}
        isDirectory={isDir}
        handleClose={() => setDeleteOpen(false)}
      />
    </>
  );
};

const BackRow = ({ href }: { href: string }) => (
  <Link
    to={href}
    className={cn(
      "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm",
      "transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    )}
  >
    <Folder className="size-4 shrink-0 text-primary" aria-hidden="true" />
    <span className="flex-1 text-muted-foreground">..</span>
    <span className="w-20" />
    <span className="w-32" />
    <span className="w-7" />
  </Link>
);

interface FolderBreadcrumbMenuProps {
  prefix: DatasetFileConfig;
  filePath: string;
}

const FolderBreadcrumbMenu = ({ prefix, filePath }: FolderBreadcrumbMenuProps) => {
  const navigate = useNavigate();
  const user = useUser();
  const routerPath = encodeObjectKey(resolveRouterPath(prefix.prefix));
  const objectKey = resolveObjectKey(filePath || routerPath, user?.email ?? "");
  const canWrite = useFileAccess(objectKey, "write");
  const canModify =
    canWrite && !BASE_DIRECTORIES[routerPath] && !PROJECT_PREFIX_REGEX.test(routerPath);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [fileAction, setFileAction] = useState<JobType | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const title = getPrefixTitle(prefix.prefix);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-foreground hover:bg-accent hover:text-foreground/80 focus-visible:outline-none">
            {title}
            <ChevronDown className="size-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
          {canWrite && (
            <DropdownMenuItem onClick={() => setNewFolderOpen(true)}>New Folder</DropdownMenuItem>
          )}
          {canWrite && (
            <DropdownMenuItem onClick={() => setUploadOpen(true)}>Upload Files...</DropdownMenuItem>
          )}
          {canWrite && <DropdownMenuSeparator />}
          <DropdownMenuItem onClick={() => setFileAction(JobType.COPY)}>
            Copy Folder...
          </DropdownMenuItem>
          {canModify && (
            <DropdownMenuItem onClick={() => setFileAction(JobType.RENAME)}>
              Rename...
            </DropdownMenuItem>
          )}
          {canModify && (
            <DropdownMenuItem onClick={() => setFileAction(JobType.MOVE)}>Move...</DropdownMenuItem>
          )}
          {canWrite && <DropdownMenuSeparator />}
          <DropdownMenuItem onClick={() => navigator.clipboard.writeText(window.location.href)}>
            <Copy />
            Copy link
          </DropdownMenuItem>
          {canModify && <DropdownMenuSeparator />}
          {canModify && (
            <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateFolderDialog
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        prefix={objectKey.endsWith("/") ? objectKey : `${objectKey}/`}
      />
      <UploadFileDialog
        uploadPrefix={objectKey.endsWith("/") ? objectKey : `${objectKey}/`}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />
      {fileAction && (
        <FileActionDialog
          open
          fileKey={objectKey}
          action={fileAction}
          isDirectory
          handleClose={() => setFileAction(null)}
        />
      )}
      <DeleteFileDialog
        open={deleteOpen}
        fileKey={objectKey.endsWith("/") ? objectKey : `${objectKey}/`}
        isDirectory
        handleClose={(deleted) => {
          setDeleteOpen(false);
          if (deleted) {
            navigate("..", { relative: "path" });
          }
        }}
      />
    </>
  );
};

const SubDirectory = ({
  prefixId,
  fileConfigs,
}: {
  prefixId: string;
  fileConfigs: DatasetFileConfig[];
}) => {
  const { datasetId, "*": filePath = "" } = useParams();
  const { state, search } = useLocation();
  const navigate = useNavigate();
  const preview = state?.preview as Pick<FileInfo, "key" | "size"> | undefined;
  const user = useUser();
  const canPreviewFile = useUserPermission("create:file-browser-download");
  const objectKey = resolveObjectKey(filePath, user?.email ?? "");
  const { data, isLoading } = useFiles(objectKey ? `${objectKey}/` : undefined);

  // Determine back link: if we're at the top of the prefix, go to root; otherwise go up one level
  const prefixRouterPaths = fileConfigs.map(({ prefix }) => resolveRouterPath(prefix));
  const atPrefixRoot = prefixRouterPaths.includes(filePath);
  const backHref = atPrefixRoot
    ? `/nextgen/datasets/${datasetId}/files`
    : `/nextgen/datasets/${datasetId}/files/${prefixId}/${filePath
        .split("/")
        .slice(0, -1)
        .join("/")}`.replace(/\/$/, "");

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading files" className="flex flex-col gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 rounded-md" />
        ))}
      </div>
    );
  }

  const children = data?.children ?? [];

  return (
    <div className="flex flex-col">
      <DirectoryHeader />
      <BackRow href={backHref} />
      {children.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">This folder is empty.</p>
      ) : (
        children.map((node) => {
          const routerPath = encodeObjectKey(resolveRouterPath(node.key));
          const href = `/nextgen/datasets/${datasetId}/files/${prefixId}/${routerPath}`;
          return <FileItemRow key={node.key} node={node} href={href} disabled={!canPreviewFile} />;
        })
      )}
      {preview && (
        <FilePreview
          file={preview}
          onClose={() => navigate({ search }, { replace: true, state: null })}
          displayMoreActions={false}
        />
      )}
    </div>
  );
};

export const DatasetFiles = () => {
  const navigate = useNavigate();
  const {
    datasetId,
    prefixId,
    "*": filePath,
  } = useParams<{ datasetId: string; prefixId: string; "*": string }>();
  const user = useUser();
  const { data: dataset } = useDataset();
  const { data: fileConfigs, isLoading, mutate: mutateConfigs } = useDatasetPrefixes();
  const isPreview = dataset?.access_level === DatasetAccessLevel.PREVIEW;
  const canEdit = !!dataset?.actions_permitted.includes("edit:catalog-metadata");
  const uploadObjectKey = resolveObjectKey(filePath ?? "", user?.email ?? "");
  const canWrite = useFileAccess(uploadObjectKey, "write");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [addFolderOpen, setAddFolderOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [addFolderSubmitting, setAddFolderSubmitting] = useState(false);
  const [addFolderError, setAddFolderError] = useState("");

  const selectedPrefix = fileConfigs?.find(({ id }) => id === prefixId);

  const handleAddFolder = async () => {
    if (!selectedFile) return;
    setAddFolderError("");
    setAddFolderSubmitting(true);
    try {
      await apiMutate(`${VITE_CATALOG_API}/datasets/${datasetId}/file-configs`, "POST", {
        prefix: selectedFile.key,
      });
      trackEvent("dataset_file_config_added", {
        dataset_id: datasetId,
        folder_prefix: selectedFile.key,
      });
      await mutateConfigs();
      setAddFolderOpen(false);
      setSelectedFile(null);
    } catch {
      setAddFolderError("There was a problem trying to add the folder to the dataset");
    }
    setAddFolderSubmitting(false);
  };

  const handleAddFolderClose = () => {
    setAddFolderOpen(false);
    setSelectedFile(null);
    setAddFolderError("");
  };

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading files"
        className="mx-auto flex w-full max-w-3xl flex-col gap-2"
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 rounded-md" />
        ))}
      </div>
    );
  }

  if (isPreview) {
    return (
      <p className="py-16 text-center text-sm text-muted-foreground">
        Files are not available for this dataset.
      </p>
    );
  }

  const isEmpty = !fileConfigs || fileConfigs.length === 0;

  return (
    <>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
        {isEmpty ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {canEdit
                ? "This dataset has no files. Add a folder below to get started."
                : "This dataset has no files."}
            </p>
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/80"
                onClick={() => setAddFolderOpen(true)}
              >
                <Plus />
                Add Folder
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              {prefixId ? (
                <nav aria-label="Breadcrumbs">
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem>
                        <BreadcrumbLink
                          href={`/nextgen/datasets/${datasetId}/files`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/nextgen/datasets/${datasetId}/files`);
                          }}
                        >
                          All Files
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      {selectedPrefix && (
                        <>
                          <BreadcrumbSeparator />
                          <BreadcrumbItem>
                            <FolderBreadcrumbMenu
                              prefix={selectedPrefix}
                              filePath={filePath ?? ""}
                            />
                          </BreadcrumbItem>
                        </>
                      )}
                    </BreadcrumbList>
                  </Breadcrumb>
                </nav>
              ) : (
                <h1 className="text-2xl font-semibold">Files</h1>
              )}
              {!prefixId && canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-primary hover:text-primary/80"
                  onClick={() => setAddFolderOpen(true)}
                >
                  <Plus />
                  Add Folder
                </Button>
              )}
              {prefixId && canWrite && (
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:text-primary/80"
                    onClick={() => setNewFolderOpen(true)}
                  >
                    <FolderPlus />
                    New Folder
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:text-primary/80"
                    onClick={() => setUploadOpen(true)}
                  >
                    <Upload />
                    Upload Files
                  </Button>
                </div>
              )}
            </div>
            <UploadFileDialog
              uploadPrefix={uploadObjectKey.endsWith("/") ? uploadObjectKey : `${uploadObjectKey}/`}
              open={uploadOpen}
              onClose={() => setUploadOpen(false)}
            />
            <CreateFolderDialog
              open={newFolderOpen}
              onClose={() => setNewFolderOpen(false)}
              prefix={uploadObjectKey.endsWith("/") ? uploadObjectKey : `${uploadObjectKey}/`}
            />
            {prefixId ? (
              <SubDirectory prefixId={prefixId} fileConfigs={fileConfigs} />
            ) : (
              <div className="flex flex-col">
                <DirectoryHeader />
                {fileConfigs.map((config) => (
                  <PrefixRow
                    key={config.id}
                    config={config}
                    datasetId={datasetId ?? ""}
                    canEdit={canEdit}
                    onRemoved={() => mutateConfigs()}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {canEdit && (
        <Dialog
          open={addFolderOpen}
          onOpenChange={(open) => {
            if (!open) handleAddFolderClose();
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Folder</DialogTitle>
            </DialogHeader>
            {addFolderError && <p className="text-sm text-destructive">{addFolderError}</p>}
            <FileTree
              orgFilesOnly
              checkWriteAccess
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
            <DialogFooter>
              <Button
                variant="outline"
                disabled={addFolderSubmitting}
                onClick={handleAddFolderClose}
              >
                Cancel
              </Button>
              <Button disabled={!selectedFile || addFolderSubmitting} onClick={handleAddFolder}>
                {addFolderSubmitting ? "Adding..." : "Add Folder"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
